import { Avatar, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import { fromNow } from '../utils/dateTimeUtils';
import { ActionMenu } from './ActionMenu';
import auth from '@client/components/auth';
import { useState } from 'react';
import { UserEmailModal } from './UserEmailModal';

type PostAuthor = {
  id: number;
  username: string;
  badges: string[];
  profilePicture: string;
  email: string;
  marketing: boolean;
};

type Props = {
  communityId: number;
  isUserCommunityOwner: boolean;
  author: PostAuthor;
  createdAt: Date;
  id: string;
  onDelete: () => void;
  onBlock: (communityId: number, userId: number) => void;
  onReplyInDM?: () => void;
  isPinned?: boolean;
  onPin?: () => void;
  pinLimitReached?: boolean;
};

export const PostHeader = ({
  communityId,
  isUserCommunityOwner,
  author,
  createdAt,
  onDelete,
  onBlock,
  onReplyInDM,
  isPinned,
  onPin,
  pinLimitReached,
}: Props) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const isAdmin = auth.isAdministrator();
  const isAuthor: boolean = author.badges.includes('own');
  const isCreatorsPost: boolean = author.badges.includes('creator');
  const userId = author.id;
  const showBlockUser =
    ((isUserCommunityOwner && !isAuthor) || isAdmin) && !isCreatorsPost;
  const showPinAction = isUserCommunityOwner || isAdmin;

  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-2.5">
        <Avatar
          alt={author.username}
          src={author.profilePicture || ''}
          data-testid="avatar"
          onClick={(ev) => {
            ev.stopPropagation();
            setIsProfileOpen(true);
          }}
          className="cursor-pointer"
        />
        <div className="flex flex-col justify-center">
          <div
            className="flex gap-1.5 items-center"
            onClick={(ev) => {
              ev.stopPropagation();
              setIsProfileOpen(true);
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, cursor: 'pointer' }}
            >
              {author.username}
            </Typography>
            {author.badges.includes('creator') && (
              <FontAwesomeIcon
                icon={faBadgeCheck}
                className="text-purple-1"
                data-testid="badge"
              />
            )}
          </div>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '0.75rem',
              lineHeight: '0.938rem',
            }}
          >
            {fromNow(createdAt)}
          </Typography>
        </div>
      </div>
      {(isUserCommunityOwner || isAuthor || isAdmin) && (
        <ActionMenu
          onDelete={onDelete}
          onBlock={() => onBlock(communityId, userId)}
          item="post"
          showBlockUser={showBlockUser}
          userEmail={author.email}
          onReplyInDM={onReplyInDM}
          showPinAction={showPinAction}
          isPinned={isPinned}
          onPin={onPin}
          pinLimitReached={pinLimitReached}
        />
      )}
      {isProfileOpen && (
        <UserEmailModal
          open={isProfileOpen}
          onClose={() => setIsProfileOpen(false)}
          userId={author.id}
          username={author.username}
          profilePicture={author.profilePicture}
          email={author.email}
          marketing={author.marketing}
          onReplyInDM={onReplyInDM}
        />
      )}
    </div>
  );
};
